import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Upload, FileText, CheckCircle2, AlertTriangle, Sparkles, Zap, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { refreshQuota } from "./QuotaBadge";
import { UploadRemainingBanner } from "./UploadRemainingBanner";
import { compressImageIfNeeded } from "../lib/imageCompress";

/**
 * Dialog per caricare il PDF del menu del ristorante. Il backend usa Gemini
 * 2.5 Flash per estrarre piatti, prezzi e ingredienti ipotizzati, creando
 * automaticamente le ricette e gli ingredienti mancanti.
 */
export function MenuImportDialog({ open, onClose, onImported }) {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [quotaExhausted, setQuotaExhausted] = useState(false);
    const [compressing, setCompressing] = useState(false);
    const inputRef = useRef(null);
    const cameraRef = useRef(null);
    const navigate = useNavigate();

    if (!open) return null;

    const reset = () => {
        setFile(null);
        setResult(null);
        setError("");
        setQuotaExhausted(false);
        setCompressing(false);
    };

    const handleClose = () => {
        if (busy) return;
        reset();
        onClose();
    };

    const handleFile = async (f) => {
        if (!f) return;
        if (f.size > 25 * 1024 * 1024) {
            toast.error("File troppo grande (max 25 MB). Comprimilo o riduci la risoluzione.");
            return;
        }
        setError("");
        setResult(null);
        // Comprime automaticamente immagini > 3 MB lato client
        if (f.type.startsWith("image/") && f.size > 3 * 1024 * 1024) {
            setCompressing(true);
            try {
                const compressed = await compressImageIfNeeded(f);
                if (compressed.size < f.size) {
                    const savedKb = Math.round((f.size - compressed.size) / 1024);
                    toast.success(`Foto ottimizzata (-${savedKb} KB) per upload più veloce`);
                }
                setFile(compressed);
            } catch {
                setFile(f);
            } finally {
                setCompressing(false);
            }
        } else {
            setFile(f);
        }
    };

    const upload = async () => {
        if (!file) return;
        setBusy(true);
        setError("");
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/recipes/import-menu", form, {
                headers: { "Content-Type": "multipart/form-data" },
                // 110s lato client: il backend ha un wait_for interno a 85s che
                // ritorna 504 prima che il proxy K8s tagli (idle ~120s).
                // 25s di margine per upload del file e parsing risposta.
                timeout: 110000,
            });
            setResult(data);
            toast.success(`${data.added_recipes} piatti importati dal menu`);
            refreshQuota();
            onImported?.();
        } catch (e) {
            const status = e?.response?.status;
            const code = e?.code;
            let msg;
            if (code === "ECONNABORTED" || code === "ERR_NETWORK" || /timeout/i.test(e?.message || "")) {
                msg = "L'analisi del menu è troppo lunga. Consigli: 1) usa un PDF con meno pagine (max 3-4), 2) oppure fai una foto nitida solo delle pagine rilevanti, 3) riduci il peso del file (ideale ≤ 2 MB).";
            } else if (status === 504) {
                msg = formatApiError(e) || "Timeout AI: prova con un file più piccolo o una foto del menu.";
            } else if (status === 402) {
                setQuotaExhausted(true);
                msg = formatApiError(e);
            } else {
                msg = formatApiError(e);
            }
            setError(msg);
            toast.error(msg);
            refreshQuota();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4 py-6"
            onClick={handleClose}
            data-testid="menu-import-dialog"
        >
            <div
                className="w-full max-w-2xl max-h-[90vh] bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-6 py-5 border-b border-brand-line flex items-start justify-between gap-4 bg-brand-cream">
                    <div>
                        <div className="eyebrow text-brand-gold flex items-center gap-1.5">
                            <Sparkles size={11} /> Importazione AI
                        </div>
                        <h2 className="font-display text-3xl text-brand-ink mt-1">
                            Carica il tuo menu PDF
                        </h2>
                        <p className="mt-1 text-[13px] text-brand-muted">
                            L'AI estrae piatti, categorie, prezzi e ingredienti.
                            I piatti e gli ingredienti mancanti vengono creati automaticamente.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={busy}
                        className="h-9 w-9 rounded-full text-brand-muted hover:bg-white transition flex items-center justify-center shrink-0 disabled:opacity-50"
                        data-testid="menu-import-close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {!result ? (
                        <>
                            <div className="mb-4">
                                <UploadRemainingBanner kind="menus" label="Menu PDF" />
                            </div>
                            <div
                                onClick={() => !busy && !compressing && inputRef.current?.click()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (busy || compressing) return;
                                    handleFile(e.dataTransfer.files?.[0]);
                                }}
                                onDragOver={(e) => e.preventDefault()}
                                className="rounded-xl border-2 border-dashed border-brand-line bg-white hover:border-brand-gold/60 hover:bg-brand-cream transition cursor-pointer px-6 py-10 text-center"
                                data-testid="menu-import-dropzone"
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                                    className="hidden"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                    data-testid="menu-import-file-input"
                                />
                                {/* Input dedicato fotocamera (mobile) */}
                                <input
                                    ref={cameraRef}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    className="hidden"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                    data-testid="menu-import-camera-input"
                                />
                                <div className="mx-auto h-14 w-14 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center mb-3">
                                    {compressing ? (
                                        <Loader2 size={24} className="animate-spin" />
                                    ) : file ? (
                                        <FileText size={24} />
                                    ) : (
                                        <Upload size={24} />
                                    )}
                                </div>
                                {compressing ? (
                                    <div className="font-display text-lg text-brand-ink">Ottimizzazione foto…</div>
                                ) : file ? (
                                    <>
                                        <div className="font-semibold text-brand-ink break-all">{file.name}</div>
                                        <div className="text-[12px] text-brand-muted mt-1">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB · pronto per l'upload
                                        </div>
                                        {file.size > 5 * 1024 * 1024 && (
                                            <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-block">
                                                ⚠️ File &gt; 5 MB: l'analisi AI può impiegare fino a 85 s. Se va in timeout, prova a comprimerlo o a fotografare solo le pagine rilevanti.
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="font-display text-lg text-brand-ink">
                                            Trascina qui il tuo menu
                                        </div>
                                        <div className="text-[12px] text-brand-muted mt-1">
                                            oppure usa i pulsanti qui sotto · PDF, JPG, PNG · max 25 MB (ideale &lt; 2 MB)
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => !busy && !compressing && inputRef.current?.click()}
                                    disabled={busy || compressing}
                                    data-testid="menu-pick-file-btn"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-brand-line bg-paper text-sm font-semibold text-brand-ink hover:border-brand-gold/60 hover:bg-brand-gold/5 transition disabled:opacity-50"
                                >
                                    <Upload size={14} /> Scegli file
                                </button>
                                <button
                                    type="button"
                                    onClick={() => !busy && !compressing && cameraRef.current?.click()}
                                    disabled={busy || compressing}
                                    data-testid="menu-pick-camera-btn"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-brand-gold/60 bg-brand-gold/10 text-sm font-bold text-brand-navy hover:bg-brand-gold/20 transition disabled:opacity-50"
                                >
                                    <Camera size={14} /> Scatta foto
                                </button>
                            </div>

                            {error && (
                                <div className="mt-4 rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-3 flex items-start gap-2">
                                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {quotaExhausted && (
                                <div className="mt-4 rounded-xl border-2 border-brand-gold/50 bg-brand-gold/10 px-4 py-4">
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-full bg-brand-gold/30 text-brand-gold flex items-center justify-center shrink-0">
                                            <Zap size={18} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-display text-lg text-brand-ink">
                                                Acquista una ricarica
                                            </div>
                                            <div className="mt-1 text-[13px] text-brand-ink/80">
                                                100 upload extra (80 fatture + 5 menu + 15 ricette AI) a 10€ una tantum. I crediti non scadono.
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onClose();
                                                    navigate("/billing");
                                                }}
                                                data-testid="menu-quota-buy-topup"
                                                className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-4 py-2 text-[12px] font-bold hover:bg-brand-goldDark transition"
                                            >
                                                <Sparkles size={12} /> Vai alla ricarica
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="mt-5 rounded-lg bg-brand-navy/5 border border-brand-navy/10 px-4 py-3 text-[12px] text-brand-ink/80 space-y-1">
                                <div><strong>Cosa succede quando carichi:</strong></div>
                                <div>• Ogni piatto del menu diventa una ricetta (1 porzione).</div>
                                <div>• Il prezzo di vendita viene estratto automaticamente.</div>
                                <div>• Gli ingredienti non presenti vengono creati in "dispensa" con costo 0 (da aggiornare).</div>
                                <div>• I piatti già esistenti con lo stesso nome vengono saltati.</div>
                            </div>
                        </>
                    ) : (
                        <ResultView result={result} />
                    )}
                </div>

                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex items-center justify-end gap-2">
                    {result ? (
                        <>
                            <button
                                type="button"
                                onClick={reset}
                                className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                            >
                                Carica un altro menu
                            </button>
                            <button
                                type="button"
                                onClick={handleClose}
                                data-testid="menu-import-done"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition"
                            >
                                Fatto
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={busy}
                                className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={upload}
                                disabled={!file || busy}
                                data-testid="menu-import-upload-btn"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-gold text-brand-navy text-sm font-bold hover:bg-brand-goldDark disabled:opacity-50 transition shadow-gold"
                            >
                                {busy ? (
                                    <>
                                        <span className="h-4 w-4 rounded-full border-2 border-brand-navy border-t-transparent animate-spin" />
                                        Analisi in corso…
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={14} /> Analizza menu
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function ResultView({ result }) {
    const { added_recipes, added_ingredients, skipped_recipes, total_dishes_detected } = result;
    return (
        <div className="space-y-4" data-testid="menu-import-result">
            <div className="text-center py-6">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 text-brand-success flex items-center justify-center mb-3">
                    <CheckCircle2 size={28} strokeWidth={1.8} />
                </div>
                <div className="font-display text-3xl text-brand-ink">
                    {added_recipes} piatti importati
                </div>
                <div className="text-[13px] text-brand-muted mt-1">
                    su {total_dishes_detected} rilevati nel menu
                </div>
            </div>

            {added_ingredients.length > 0 && (
                <SummarySection
                    title={`${added_ingredients.length} ingredienti creati`}
                    items={added_ingredients}
                    muted="Aggiornali con costo e fornitore in /ingredients"
                />
            )}

            {skipped_recipes.length > 0 && (
                <SummarySection
                    title={`${skipped_recipes.length} piatti saltati`}
                    items={skipped_recipes}
                    muted="Già presenti o senza ingredienti riconosciuti"
                />
            )}
        </div>
    );
}

function SummarySection({ title, items, muted }) {
    return (
        <div className="rounded-xl border border-brand-line bg-white px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted font-bold mb-2">
                {title}
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {items.slice(0, 40).map((name, i) => (
                    <span
                        key={i}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-brand-cream text-brand-ink/80"
                    >
                        {name}
                    </span>
                ))}
                {items.length > 40 && (
                    <span className="text-[11px] px-2 py-0.5 text-brand-muted">
                        …e altri {items.length - 40}
                    </span>
                )}
            </div>
            {muted && (
                <div className="mt-2 text-[11px] text-brand-muted italic">{muted}</div>
            )}
        </div>
    );
}
